import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog';
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { createHandleChangeApostrophe, trimInput, removeTrailingSpacesAndDashes } from "../../../Components/CleanInputs";
import { useTranslation, Trans } from "react-i18next";
import CustomToast from '../../../Components/CustomToast';
import { normalizeTagGroupsList } from "../Segments/tagUtils";
import { useSelector, useDispatch } from "react-redux";
import "../../../Styles/components/tags.scss";
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

export const EditTagGroup = ({ visible, hideEditTagGroupPopup, getSummaryCustomers, getTagGroups, tagGroups, pageOptions, initialTagGroup = null }) => {

    const [toastTrigger, setToastTrigger] = useState(false);
    const [tagGroupValues, setTagGroupValues] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [updatedName, setUpdatedName] = useState("");
    const [showTagErrorMessage, setShowTagErrorMessage] = useState(false);
    const [finalGroupName, setFinalGroupName] = useState("");

    const [state, setState] = useState({
        updating: false
    })

    const [tagToast, setTagToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });

    const showTagToast = (severity, summary, detail) => {
        setTagToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setToastTrigger(true);
    }

    const [formData, setFormData] = useState({
        name: tagGroupValues?.name,
    });

    const [errors, setFieldErrors] = useState({
        name: ''
    });

    const { t } = useTranslation("common");

    const handleChange = createHandleChangeApostrophe(setFormData, setFieldErrors, t);

    const handleDropdownChange = (e) => {
        setTagGroupValues(e.value);
        setFinalGroupName(e.value.name);
        setUpdatedName(e.value);
        setShowTagErrorMessage(false);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setFinalGroupName(value);
        handleChange('name')({ target: { name: 'name', value } });
    };

    //redux state from store
    const { updatingTagGroup, updatedTagGroup, updateTagGroupError } = useSelector((state) => state.businessCustomers);
    const defMembership = useSelector((state) => state.defMembership.defMembership);

    const dispatch = useDispatch();
    const updateTagGroup = useCallback((groupId, tagGroup) => dispatch(BusinessCustomersActions.updateTagGroupRequest(groupId, tagGroup)), [dispatch]);
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);

    const editTagGroupError = updateTagGroupError?.detail?.split('"')[1] ? updateTagGroupError?.detail?.split('"')[1] : updateTagGroupError?.detail

    useEffect(() => {
        if (!updatingTagGroup && state.updating && updatedTagGroup) {
            showTagToast("success", t("tags.success"), t("tags.tag_group_edit_success"))
        }
        if (!updatingTagGroup && state.updating && updateTagGroupError) {
            showTagToast("error", t("tags.error"), editTagGroupError)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateTagGroupError, updatingTagGroup, state.updating, updatedTagGroup]);

    useEffect(() => {
        if (tagGroupValues) {
            setUpdatedName(tagGroupValues?.name)
        }
    }, [tagGroupValues])

    useEffect(() => {
        if (visible && initialTagGroup?.id) {
            setTagGroupValues(initialTagGroup);
            setFinalGroupName(initialTagGroup.name ?? "");
            setUpdatedName(initialTagGroup.name ?? "");
        }
    }, [visible, initialTagGroup])

    const tagGroupOptions = useMemo(() => {
        const groups = normalizeTagGroupsList(tagGroups);
        if (!groups.length) {
            return;
        }
        return groups.filter((group) => group.name && group.id);
    }, [tagGroups]);

    const isTagGroupLocked = Boolean(initialTagGroup?.id);
    const contextGroupName = tagGroupValues?.name ?? initialTagGroup?.name ?? "";

    const onSubmit = () => {
        const tagGroupId = tagGroupValues.id;
        setState((state) => { return { ...state, updating: true } });
        const tagGroup = {
            name: removeTrailingSpacesAndDashes(finalGroupName),
            id: tagGroupId,
            business: { id: defMembership.business.id }
        }
        updateTagGroup(tagGroupId, tagGroup);
        setFinalGroupName(tagGroup.name);
        // eslint-disable-next-line no-unused-vars
        const trimmedData = {
            name: trimInput(formData.name)
        };
    }

    const isButtonDisabled = !tagGroupValues || !finalGroupName || updatingTagGroup || toastTrigger || !!errors.name || tagGroupValues.name === finalGroupName;

    return (
        <>
            <CustomToast
                {...tagToast}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    hideEditTagGroupPopup();
                    getSummaryCustomers(defMembership.business.id, pageOptions);
                    getTagGroups(defMembership.business.id);
                    getTagGroupsGraphed(defMembership.business.id)
                    setState((state) => { return { ...state, updating: false } })
                    setTagGroupValues(null);
                    setUpdatedName("");
                    setFieldErrors({ name: '' });
                    setShowTagErrorMessage(false);
                    setFinalGroupName("");
                }}
                handleActionsAfterErrorToast={() => {
                    setShowTagErrorMessage(true);
                    setToastTrigger(false);
                }}
            />

            <Dialog
                header={t("tags.edit_tag_group")}
                visible={visible}
                style={{ width: "350px" }}
                id="tier_dialog"
                className="tier_dialog"
                onHide={() => {
                    hideEditTagGroupPopup();
                    setTagGroupValues(null);
                    setUpdatedName("");
                    setFinalGroupName("");
                    setFieldErrors({ name: '' });
                    setFormData({});
                }}
            >
                <div className="p-grid p-fluid" style={{ margin: "0 auto 1rem" }}>
                    <div className="edit_forms_div">
                        <div className="label_inputs">
                            {isTagGroupLocked ? (
                                <div className="tags-dialog-tag-group-context mb-3">
                                    <span className="createdOnLabel">
                                        {t("tags.tag_group_label")}
                                        {":"}
                                    </span>
                                    <span className="name-body">{contextGroupName}</span>
                                </div>
                            ) : (
                                <>
                                    <label
                                        id='name_label'
                                        htmlFor="name"
                                        className="product-form-label">{t("tags.choose")}
                                    </label>
                                    <div className='flex justify-content-flex-start align-items-center'>
                                        <Dropdown
                                            value={tagGroupValues}
                                            options={tagGroupOptions}
                                            optionLabel="name"
                                            placeholder={t("tags.select_tag_group")}
                                            className=" justify-content-center align-items-center tag-group-edit-dropdown large-tag-group-dropdown"
                                            onChange={handleDropdownChange}
                                        />
                                    </div>
                                </>
                            )}
                            <div className='grid mt-3 tag-footer-elements'>
                                <div className='col-9'>
                                    <InputText
                                        value={finalGroupName}
                                        onChange={handleInputChange}
                                        placeholder={t("tags.enter_tag_group_name")}
                                        disabled={!isTagGroupLocked && !tagGroupValues}
                                        maxLength={25}
                                        className='edit-tag-group-input'
                                    />
                                </div>

                                <div className='col-3'>
                                    <Button
                                        id="edit-tag-group-button"
                                        className='save-btns'
                                        label={t("business_customers.update")}
                                        disabled={isButtonDisabled}
                                        onClick={(data) => {
                                            onSubmit(data);
                                        }}
                                        autoFocus
                                        icon={updatingTagGroup ? "pi pi-spinner pi-spin" : null}
                                        style={{
                                            position: 'relative'
                                        }}
                                    />
                                </div>
                            </div>
                            {errors.name && <p className="error-message">{errors.name}</p>}

                            {showTagErrorMessage ?
                                <div className='mt-1'>
                                    <Trans
                                        i18nKey="tags.message_group_name_exists"
                                        values={{
                                            groupName: finalGroupName
                                        }}
                                        components={{
                                            1: <span className="tags_error_message" />,
                                            2: <span className="tags_error_message_bold" />,
                                            3: <span className="tags_error_message" />
                                        }}
                                    />
                                </div>
                                : null}
                        </div>
                    </div>
                </div>
            </Dialog >
        </>
    )
}